/**
 * Activity report — pure helpers (no React, no Firestore):
 *  - date-range presets ("today", "yesterday", "this week", …) in the admin's
 *    local time, because that is the day the person reading the report means;
 *  - aggregation of activity_logs per dealer, or per dealer per day;
 *  - the row shapes the CSV exports use.
 *
 * Volume note: the customer app dedupes events per session and records
 * app_open at most hourly, so a dealer is ~10 log docs per session. A month for
 * ~100 dealers is a few tens of thousands of docs — fetched once, aggregated
 * here in the browser. If the dealer base grows into the thousands this should
 * move to a daily roll-up written by the worker; the shapes below are designed
 * so only the data source changes.
 */
import type { User } from '@mitang/shared';
import type { ActivityLogWithId } from './collections';
import { EVENT_LABEL, categoryOf, modelNumberOf, productIdOf, productLabelOf, queryOf } from './activityMeta';
import { dayKey } from './time';

/* --------------------------------- ranges --------------------------------- */

export type RangePreset =
  | 'today'
  | 'yesterday'
  | 'twoDaysAgo'
  | 'thisWeek'
  | 'last7'
  | 'thisMonth'
  | 'last30'
  | 'custom';

export const RANGE_PRESETS: ReadonlyArray<{ key: RangePreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'twoDaysAgo', label: '2 days ago' },
  { key: 'thisWeek', label: 'This week' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom…' },
];

export interface DateRange {
  /** Inclusive epoch ms bounds, local-midnight aligned. */
  from: number;
  to: number;
  label: string;
}

/** Local midnight of the day containing `ts`, shifted by `dayOffset` calendar days (DST-safe). */
function dayStart(ts: number, dayOffset = 0): number {
  const d = new Date(ts);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dayOffset).getTime();
}

/** Last millisecond of the day containing `ts`. */
function dayEnd(ts: number): number {
  return dayStart(ts, 1) - 1;
}

const dayFmt = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
const shortFmt = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export function describeRange(range: DateRange): string {
  const sameDay = dayKey(range.from) === dayKey(range.to);
  return sameDay ? dayFmt.format(new Date(range.from)) : `${shortFmt.format(new Date(range.from))} → ${shortFmt.format(new Date(range.to))}`;
}

/** The bounds a preset means right now. `null` for custom (bounds come from the inputs). */
export function presetRange(preset: RangePreset, now: number = Date.now()): DateRange | null {
  switch (preset) {
    case 'today':
      return { from: dayStart(now), to: dayEnd(now), label: 'Today' };
    case 'yesterday':
      return { from: dayStart(now, -1), to: dayEnd(dayStart(now, -1)), label: 'Yesterday' };
    case 'twoDaysAgo':
      return { from: dayStart(now, -2), to: dayEnd(dayStart(now, -2)), label: '2 days ago' };
    case 'thisWeek': {
      // Monday → now (Indian business week).
      const dow = (new Date(now).getDay() + 6) % 7;
      return { from: dayStart(now, -dow), to: dayEnd(now), label: 'This week' };
    }
    case 'last7':
      return { from: dayStart(now, -6), to: dayEnd(now), label: 'Last 7 days' };
    case 'thisMonth': {
      const d = new Date(now);
      return { from: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), to: dayEnd(now), label: 'This month' };
    }
    case 'last30':
      return { from: dayStart(now, -29), to: dayEnd(now), label: 'Last 30 days' };
    case 'custom':
      return null;
  }
}

/** Bounds from two `YYYY-MM-DD` inputs (local). `to` defaults to `from`. */
export function customRange(fromDate: string, toDate: string): DateRange | null {
  if (!fromDate) return null;
  const from = new Date(`${fromDate}T00:00:00`).getTime();
  if (Number.isNaN(from)) return null;
  const toBase = toDate ? new Date(`${toDate}T00:00:00`).getTime() : from;
  if (Number.isNaN(toBase) || toBase < from) return null;
  return { from, to: dayEnd(toBase), label: 'Custom' };
}

/** `YYYY-MM-DD` for a date input, local. */
export function inputDate(ts: number): string {
  return dayKey(ts);
}

/* ------------------------------ aggregation ------------------------------- */

export interface DealerActivityRow {
  /** userId, or `${userId}|${day}` when grouped by day. */
  key: string;
  user: User;
  /** `YYYY-MM-DD` when grouped by day. */
  day?: string;
  events: number;
  /** app_open events (recorded at most hourly per device). */
  sessions: number;
  productViews: number;
  distinctProducts: number;
  categoryViews: number;
  distinctCategories: number;
  searches: number;
  shares: number;
  wishlisted: number;
  firstTs: number | null;
  lastTs: number | null;
  /** Distinct products viewed, as the labels people read (model no, else id). */
  products: string[];
  categories: string[];
}

interface Acc {
  row: DealerActivityRow;
  products: Set<string>;
  categories: Set<string>;
}

function emptyRow(key: string, user: User, day?: string): DealerActivityRow {
  return {
    key, user, day,
    events: 0, sessions: 0, productViews: 0, distinctProducts: 0, categoryViews: 0,
    distinctCategories: 0, searches: 0, shares: 0, wishlisted: 0,
    firstTs: null, lastTs: null, products: [], categories: [],
  };
}

export interface AggregateOptions {
  /** One row per dealer per calendar day instead of one per dealer. */
  byDay: boolean;
  /** Also list dealers with zero events in the range (ignored when byDay). */
  includeIdle: boolean;
}

/**
 * Per-dealer counts for the logs given (already limited to the range and to
 * the selected dealers by the caller). Logs of deleted accounts are skipped.
 */
export function aggregateByDealer(
  users: User[],
  logs: ActivityLogWithId[],
  opts: AggregateOptions,
): DealerActivityRow[] {
  const byId = new Map(users.map((u) => [u.id, u]));
  const accs = new Map<string, Acc>();

  for (const log of logs) {
    const user = byId.get(log.userId);
    if (!user) continue;
    const day = opts.byDay ? dayKey(log.ts) : undefined;
    const key = day ? `${log.userId}|${day}` : log.userId;
    let acc = accs.get(key);
    if (!acc) {
      acc = { row: emptyRow(key, user, day), products: new Set(), categories: new Set() };
      accs.set(key, acc);
    }
    const r = acc.row;
    r.events++;
    r.firstTs = r.firstTs === null ? log.ts : Math.min(r.firstTs, log.ts);
    r.lastTs = r.lastTs === null ? log.ts : Math.max(r.lastTs, log.ts);
    switch (log.event) {
      case 'app_open':
        r.sessions++;
        break;
      case 'viewed_product': {
        r.productViews++;
        const label = productLabelOf(log);
        if (label) acc.products.add(label);
        const cat = categoryOf(log);
        if (cat) acc.categories.add(cat);
        break;
      }
      case 'viewed_category': {
        r.categoryViews++;
        const cat = categoryOf(log);
        if (cat) acc.categories.add(cat);
        break;
      }
      case 'searched':
        r.searches++;
        break;
      case 'shared_product': {
        r.shares++;
        const label = productLabelOf(log);
        if (label) acc.products.add(label);
        break;
      }
      case 'wishlisted':
        r.wishlisted++;
        break;
      default:
        break;
    }
  }

  const rows: DealerActivityRow[] = [];
  for (const acc of accs.values()) {
    acc.row.products = Array.from(acc.products).sort();
    acc.row.categories = Array.from(acc.categories).sort();
    acc.row.distinctProducts = acc.row.products.length;
    acc.row.distinctCategories = acc.row.categories.length;
    rows.push(acc.row);
  }
  if (opts.includeIdle && !opts.byDay) {
    for (const u of users) if (!accs.has(u.id)) rows.push(emptyRow(u.id, u));
  }
  return rows;
}

export interface ReportTotals {
  activeDealers: number;
  events: number;
  sessions: number;
  productViews: number;
  distinctProducts: number;
  searches: number;
  wishlisted: number;
}

export function reportTotals(logs: ActivityLogWithId[]): ReportTotals {
  const dealers = new Set<string>();
  const products = new Set<string>();
  const t: ReportTotals = { activeDealers: 0, events: 0, sessions: 0, productViews: 0, distinctProducts: 0, searches: 0, wishlisted: 0 };
  for (const log of logs) {
    dealers.add(log.userId);
    t.events++;
    if (log.event === 'app_open') t.sessions++;
    else if (log.event === 'viewed_product') {
      t.productViews++;
      const label = productLabelOf(log);
      if (label) products.add(label);
    } else if (log.event === 'searched') t.searches++;
    else if (log.event === 'wishlisted') t.wishlisted++;
  }
  t.activeDealers = dealers.size;
  t.distinctProducts = products.size;
  return t;
}

export interface DayRow {
  day: string;
  activeDealers: number;
  sessions: number;
  productViews: number;
  searches: number;
  events: number;
}

/** Day-by-day totals (oldest first) — the "day-wise" view of a multi-day range. */
export function aggregateByDay(logs: ActivityLogWithId[]): DayRow[] {
  const days = new Map<string, { row: DayRow; dealers: Set<string> }>();
  for (const log of logs) {
    const day = dayKey(log.ts);
    let e = days.get(day);
    if (!e) {
      e = { row: { day, activeDealers: 0, sessions: 0, productViews: 0, searches: 0, events: 0 }, dealers: new Set() };
      days.set(day, e);
    }
    e.dealers.add(log.userId);
    e.row.events++;
    if (log.event === 'app_open') e.row.sessions++;
    else if (log.event === 'viewed_product') e.row.productViews++;
    else if (log.event === 'searched') e.row.searches++;
  }
  return Array.from(days.values())
    .map((e) => ({ ...e.row, activeDealers: e.dealers.size }))
    .sort((a, b) => a.day.localeCompare(b.day));
}

/* ---------------------------------- CSV ----------------------------------- */

const PRODUCT_LIST_CAP = 200;

export function summaryCsvRow(r: DealerActivityRow, fmtDateTime: (ts: number | null) => string): Record<string, unknown> {
  return {
    day: r.day ?? '',
    dealer: r.user.name,
    company: r.user.companyName,
    city: r.user.city,
    mobile: r.user.mobile,
    email: r.user.email,
    status: r.user.status,
    sessions: r.sessions,
    productViews: r.productViews,
    distinctProducts: r.distinctProducts,
    categoriesViewed: r.distinctCategories,
    searches: r.searches,
    shares: r.shares,
    wishlisted: r.wishlisted,
    events: r.events,
    firstActivity: fmtDateTime(r.firstTs),
    lastActivity: fmtDateTime(r.lastTs),
    products: r.products.slice(0, PRODUCT_LIST_CAP).join(' | ') + (r.products.length > PRODUCT_LIST_CAP ? ` | …+${r.products.length - PRODUCT_LIST_CAP}` : ''),
    categories: r.categories.join(' | '),
  };
}

export const SUMMARY_CSV_COLUMNS = (byDay: boolean): Array<{ key: string; header: string }> => [
  ...(byDay ? [{ key: 'day', header: 'Date' }] : []),
  { key: 'dealer', header: 'Dealer' },
  { key: 'company', header: 'Company' },
  { key: 'city', header: 'City' },
  { key: 'mobile', header: 'Mobile' },
  { key: 'email', header: 'Email' },
  { key: 'status', header: 'Status' },
  { key: 'sessions', header: 'Sessions' },
  { key: 'productViews', header: 'Product views' },
  { key: 'distinctProducts', header: 'Distinct products' },
  { key: 'categoriesViewed', header: 'Categories viewed' },
  { key: 'searches', header: 'Searches' },
  { key: 'shares', header: 'Shares' },
  { key: 'wishlisted', header: 'Wishlist saves' },
  { key: 'events', header: 'Total events' },
  { key: 'firstActivity', header: 'First activity' },
  { key: 'lastActivity', header: 'Last activity' },
  { key: 'products', header: 'Products viewed' },
  { key: 'categories', header: 'Categories' },
];

export function eventCsvRow(
  log: ActivityLogWithId,
  user: User | undefined,
  fmtDateTime: (ts: number) => string,
): Record<string, unknown> {
  return {
    timestamp: fmtDateTime(log.ts),
    epochMs: log.ts,
    day: dayKey(log.ts),
    dealer: user?.name ?? log.userId,
    company: user?.companyName ?? '',
    city: user?.city ?? '',
    event: EVENT_LABEL[log.event] ?? log.event,
    modelNumber: modelNumberOf(log) ?? '',
    category: categoryOf(log) ?? '',
    productId: productIdOf(log) ?? '',
    query: queryOf(log) ?? '',
  };
}

export const EVENT_CSV_COLUMNS: Array<{ key: string; header: string }> = [
  { key: 'timestamp', header: 'Timestamp' },
  { key: 'epochMs', header: 'Epoch (ms)' },
  { key: 'day', header: 'Date' },
  { key: 'dealer', header: 'Dealer' },
  { key: 'company', header: 'Company' },
  { key: 'city', header: 'City' },
  { key: 'event', header: 'Event' },
  { key: 'modelNumber', header: 'Model no' },
  { key: 'category', header: 'Category' },
  { key: 'productId', header: 'Product ID' },
  { key: 'query', header: 'Search query' },
];

/** Safe filename fragment for a range, e.g. `2026-09-19` or `2026-09-01_2026-09-19`. */
export function rangeSlug(range: DateRange): string {
  const a = dayKey(range.from);
  const b = dayKey(range.to);
  return a === b ? a : `${a}_${b}`;
}
